import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CropIcon from "@mui/icons-material/Crop";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { useState } from "react";
import { BANNER_LIBRARY, HERO_BANNER_UPLOAD_HINT } from "@/constants/campaignBuilder";
import type { BrandingFields } from "@/context/CampaignBuilderContext";
import { useR2Upload } from "@/hooks/useR2Upload";
import type { MediaType } from "@/interfaces/uploads";
import { PillToggle } from "./PillToggle";
import { UploadCard } from "./UploadCard";

export interface HeroBannerSectionProps {
  enabled: boolean;
  bannerUrl: string;
  campaignId: string;
  /** R2 slot this banner uploads under — "banner" (email) or "landing-banner" (landing). */
  mediaType: MediaType;
  /** Saves into the email or landing branding slice (both share BrandingFields). */
  onChange: (v: Partial<BrandingFields>) => void;
  /** Opens the crop dialog for the caller's target. Omit to hide the Crop button —
   *  the landing banner is not croppable (prototype parity); the email banner is. */
  onCrop?: () => void;
}

export function HeroBannerSection({
  enabled,
  bannerUrl,
  campaignId,
  mediaType,
  onChange,
  onCrop,
}: HeroBannerSectionProps) {
  const { upload, isUploading, uploadError } = useR2Upload();
  const initialLibraryIdx = Math.max(
    0,
    BANNER_LIBRARY.findIndex((b) => b.img === bannerUrl),
  );

  // A banner is "uploaded" if it's not from the library (not a cdn.shopify.com URL)
  const isUploadedBanner = bannerUrl !== "" && !BANNER_LIBRARY.some((b) => b.img === bannerUrl);

  const [source, setSource] = useState<"library" | "upload">(
    isUploadedBanner ? "upload" : "library",
  );
  const [sliderIdx, setSliderIdx] = useState(initialLibraryIdx);
  const [bannerFileName, setBannerFileName] = useState("");

  async function handleFile(file: File) {
    setBannerFileName(file.name);
    try {
      const url = await upload(file, mediaType, campaignId, "original");
      // Fresh upload seeds both the displayed image and the crop source (see CompanyLogoSection).
      onChange({ bannerUrl: url, bannerOriginalUrl: url, bannerEnabled: true });
    } catch {
      setBannerFileName("");
    }
  }

  function handleToggle(v: boolean) {
    if (v && !bannerUrl) {
      const first = BANNER_LIBRARY[0];
      const img = first ? first.img : "";
      onChange({ bannerEnabled: true, bannerUrl: img, bannerOriginalUrl: img });
    } else {
      onChange({ bannerEnabled: v });
    }
  }

  function navigate(i: number) {
    const clamped = Math.max(0, Math.min(BANNER_LIBRARY.length - 1, i));
    setSliderIdx(clamped);
  }

  function handleSelectCurrent() {
    const entry = BANNER_LIBRARY[sliderIdx];
    if (entry) {
      onChange({ bannerUrl: entry.img, bannerOriginalUrl: entry.img });
      setSource("upload");
    }
  }

  const srcBtn = (active: boolean) =>
    [
      "flex flex-1 items-center justify-center gap-1.5 px-2.5 py-2",
      "font-lato text-[11px] font-semibold border-[1.5px] rounded-[6px] cursor-pointer transition-all",
      active
        ? "border-dark-dust bg-light-dust text-rodeo-black"
        : "border-smoke bg-white text-graphite",
    ].join(" ");

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-lato text-[13px] font-bold text-rodeo-black uppercase tracking-[0.5px]">
          Hero Banner
        </span>
        <div className="flex items-center gap-2">
          <span className="font-lato text-[11px] font-semibold uppercase tracking-[0.5px] text-graphite">
            Show
          </span>
          <PillToggle
            checked={enabled}
            onChange={(v) => handleToggle(v)}
            aria-label="Toggle hero banner"
          />
        </div>
      </div>

      {enabled && (
        <>
          {/* Source toggle */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              className={srcBtn(source === "library")}
              onClick={() => setSource("library")}
            >
              <PhotoLibraryOutlinedIcon sx={{ fontSize: 16 }} />
              Select from Library
            </button>
            <button
              type="button"
              className={srcBtn(source === "upload")}
              onClick={() => setSource("upload")}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
              Upload Your Own
            </button>
          </div>

          {/* Library panel */}
          {source === "library" && (
            <div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => navigate(sliderIdx - 1)}
                  disabled={sliderIdx === 0}
                  className={[
                    "absolute top-1/2 -translate-y-1/2 -left-0.5 w-7 h-7",
                    "rounded-full border border-smoke bg-white/92",
                    "flex items-center justify-center cursor-pointer z-2 shadow-sm transition-all",
                    sliderIdx === 0 ? "opacity-30 pointer-events-none" : "opacity-100",
                  ].join(" ")}
                  aria-label="Previous banner"
                >
                  <ChevronLeftIcon sx={{ fontSize: 18, color: "var(--color-rodeo-black)" }} />
                </button>

                <div className="overflow-hidden rounded-[6px] mx-5">
                  <div
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${sliderIdx * 100}%)` }}
                  >
                    {BANNER_LIBRARY.map((b) => (
                      <button
                        key={b.name}
                        type="button"
                        onClick={handleSelectCurrent}
                        className={[
                          "shrink-0 w-full rounded-[6px] overflow-hidden",
                          "cursor-pointer p-0 border-none block transition-opacity",
                          b.img === bannerUrl ? "opacity-100" : "opacity-70",
                        ].join(" ")}
                        aria-label={`Select ${b.name}`}
                      >
                        <img
                          src={b.img}
                          alt={b.name}
                          className="w-full aspect-video object-cover block rounded-[6px]"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(sliderIdx + 1)}
                  disabled={sliderIdx === BANNER_LIBRARY.length - 1}
                  className={[
                    "absolute top-1/2 -translate-y-1/2 -right-0.5 w-7 h-7",
                    "rounded-full border border-smoke bg-white/92",
                    "flex items-center justify-center cursor-pointer z-2 shadow-sm transition-all",
                    sliderIdx === BANNER_LIBRARY.length - 1
                      ? "opacity-30 pointer-events-none"
                      : "opacity-100",
                  ].join(" ")}
                  aria-label="Next banner"
                >
                  <ChevronRightIcon sx={{ fontSize: 18, color: "var(--color-rodeo-black)" }} />
                </button>
              </div>

              <div className="flex justify-center gap-1.25 mt-2">
                {BANNER_LIBRARY.map((b, i) => (
                  <button
                    key={b.name}
                    type="button"
                    onClick={() => navigate(i)}
                    aria-label={`Go to banner ${i + 1}`}
                    className={[
                      "w-1.5 h-1.5 rounded-full border-none p-0 cursor-pointer transition-colors",
                      i === sliderIdx ? "bg-dark-dust" : "bg-smoke",
                    ].join(" ")}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Upload panel */}
          {source === "upload" && (
            <>
              {uploadError && (
                <p className="font-lato text-[11px] text-good-wine mb-2">{uploadError}</p>
              )}
              <UploadCard
                previewUrl={isUploadedBanner ? bannerUrl : ""}
                onFile={handleFile}
                isUploading={isUploading}
                mediaType={mediaType}
                emptyLabel="Upload a hero banner"
                emptyHint={HERO_BANNER_UPLOAD_HINT}
              >
                <div className="flex items-center gap-3 mt-1">
                  <img
                    // Plain display <img> — no CORS needed to show an image. Only CropDialog reads
                    // pixels (canvas) and it does so through its own crop-only cache key.
                    src={bannerUrl}
                    alt="Banner preview"
                    className="max-w-25 max-h-8.5 object-cover border border-smoke rounded shrink-0"
                  />
                  <span className="flex-1 font-lato text-[13px] text-graphite truncate min-w-0">
                    {bannerFileName || "Uploaded"}
                  </span>
                  {onCrop && (
                    <button
                      type="button"
                      onClick={onCrop}
                      className="inline-flex items-center gap-1 font-lato text-[12px] font-semibold shrink-0 px-2.5 py-1.25 border border-smoke rounded-[6px] bg-white cursor-pointer transition-all text-graphite hover:border-dark-dust hover:text-dark-dust"
                      title="Crop banner"
                    >
                      <CropIcon sx={{ fontSize: 15 }} />
                      Crop
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ bannerUrl: "", bannerOriginalUrl: "" });
                      setBannerFileName("");
                    }}
                    className="text-graphite hover:text-good-wine transition-colors shrink-0"
                    title="Remove banner"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              </UploadCard>
            </>
          )}
        </>
      )}
    </div>
  );
}
